// "Added to your bag" bottom sheet (26 Sep 2026, David: option A from the add-to-bag mockups).
//
// On phones, an add-to-cart no longer slides the whole cart over the page. The SAME cart drawer
// opens as a sheet over the bottom of the screen showing only: the shirt just added, the 10%/15%
// offer bar and the "Add to your order" rail, with View bag / Check out / Keep shopping. View bag
// grows the sheet into today's full cart. Opening the bag from the header icon or the minibar pill
// is unchanged. Nothing is duplicated: the offer, rail, totals and checkout are today's markup, so
// they can never disagree with the cart.
//
// SPLIT TEST. Each visitor is given 'sheet' or 'drawer' (today) at random and keeps it (cookie
// kd_cartui, 60 days). The arm is written to the cart as the hidden attribute _cart_ui, so every
// order says which version its buyer saw. Force an arm with ?kdcart=sheet or ?kdcart=drawer.
// To end the test, set FORCE below to 'sheet' or 'drawer'.
import { StandardEvents } from '@shopify/events';

const FORCE = 'sheet'; // David 26 Sep: "push it through, we're using it" (everyone gets the sheet). null = 50/50 split
const PHONE = window.matchMedia('(max-width: 749px)');

function readCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}
function arm() {
  if (FORCE) return FORCE;
  let a = null;
  try { a = new URLSearchParams(location.search).get('kdcart'); } catch (e) {}
  if (a !== 'sheet' && a !== 'drawer') a = readCookie('kd_cartui');
  if (a !== 'sheet' && a !== 'drawer') a = Math.random() < 0.5 ? 'sheet' : 'drawer';
  document.cookie = 'kd_cartui=' + a + '; path=/; max-age=' + 60 * 24 * 3600 + '; samesite=lax';
  return a;
}
const ARM = arm();
document.documentElement.dataset.kdCartUi = ARM;

// tag the cart once per session so the order carries the arm
let tagged = false;
try { tagged = sessionStorage.getItem('kd_cartui_tagged') === ARM; } catch (e) {}
function tagCart() {
  if (tagged) return;
  tagged = true;
  fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _cart_ui: ARM } }),
  }).then(() => { try { sessionStorage.setItem('kd_cartui_tagged', ARM); } catch (e) {} })
    .catch(() => { tagged = false; });
}

const drawer = document.getElementById('cart-drawer');
const dialog = drawer?.querySelector('dialog');

const CHECK = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
const CLOSE = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

function build() {
  if (!dialog || dialog.querySelector('.kd-sheet-head')) return;
  const head = document.createElement('div');
  head.className = 'kd-sheet-head';
  head.innerHTML = '<div class="kd-sheet-grab" aria-hidden="true"></div>'
    + '<div class="kd-sheet-row"><p class="kd-sheet-ok">' + CHECK + '<span>Added to your bag</span></p>'
    + '<button type="button" class="kd-sheet-x" aria-label="Close">' + CLOSE + '</button></div>';
  const foot = document.createElement('div');
  foot.className = 'kd-sheet-foot';
  foot.innerHTML = '<div class="kd-sheet-btns"><button type="button" class="kd-sheet-view">View bag</button>'
    + '<button type="button" class="kd-sheet-pay">Check out</button></div>'
    + '<button type="button" class="kd-sheet-keep">Keep shopping</button>';
  dialog.prepend(head);
  // 9 Oct 2026: preview of the shirt just added, shown while the cart answers (see preview())
  const skel = document.createElement("div");
  skel.className = "kd-sheet-skel";
  skel.setAttribute("aria-hidden", "true");
  skel.innerHTML = '<div class="kd-sheet-skel__img"><img alt="" decoding="async"></div>'
    + '<div class="kd-sheet-skel__txt"><p class="kd-sheet-skel__t"></p><p class="kd-sheet-skel__v"></p><p class="kd-sheet-skel__bar"></p></div>';
  head.after(skel);
  dialog.append(foot);

  head.querySelector('.kd-sheet-x').addEventListener('click', () => drawer.close());
  foot.querySelector('.kd-sheet-keep').addEventListener('click', () => drawer.close());
  foot.querySelector('.kd-sheet-view').addEventListener('click', expand);
  foot.querySelector('.kd-sheet-pay').addEventListener('click', () => {
    const btn = dialog.querySelector('#checkout, .cart__checkout-button');
    if (btn && btn.form && btn.form.requestSubmit) btn.form.requestSubmit(btn);
    else if (btn) btn.click();
    else location.href = '/checkout';
  });

  // swipe the sheet down to close it
  let y0 = null, dy = 0;
  head.addEventListener('touchstart', (e) => { if (dialog.classList.contains('kd-sheet')) { y0 = e.touches[0].clientY; dy = 0; dialog.style.transition = 'none'; } }, { passive: true });
  head.addEventListener('touchmove', (e) => { if (y0 == null) return; dy = Math.max(0, e.touches[0].clientY - y0); dialog.style.translate = '0 ' + dy + 'px'; }, { passive: true });
  head.addEventListener('touchend', () => {
    if (y0 == null) return;
    y0 = null; dialog.style.transition = ''; dialog.style.translate = '';
    if (dy > 80) drawer.close();
  });

  new MutationObserver(label).observe(dialog, { subtree: true, childList: true, characterData: true });
  label();
}

function label() {
  const n = parseInt(dialog?.querySelector('.theme-drawer__badge')?.textContent || '0', 10) || 0;
  const v = dialog?.querySelector('.kd-sheet-view');
  const t = n ? 'View bag (' + n + ')' : 'View bag';
  if (v && v.textContent !== t) v.textContent = t;
}

// grow the sheet into the full cart in place, then hand back to the normal drawer
function expand() {
  if (!dialog.classList.contains('kd-sheet')) return;
  const h = dialog.getBoundingClientRect().height;
  dialog.style.height = h + 'px';
  dialog.classList.add('kd-sheet--growing');
  requestAnimationFrame(() => {
    dialog.style.height = window.innerHeight + 'px';
    const done = () => {
      dialog.classList.remove('kd-sheet', 'kd-sheet--growing');
      dialog.style.height = '';
      dialog.querySelector('.cart-drawer__items')?.scrollTo(0, 0);
    };
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else dialog.addEventListener('transitionend', done, { once: true });
  });
}

// The drawer opens the moment Add is pressed, before the cart has answered, so for a split second it
// still holds the PREVIOUS cart and the sheet showed the last shirt added before this one (David,
// 26 Sep: "the product that was in the cart before appears briefly, then it goes to the new one").
// Keep the contents hidden until the cart has answered and the re-render has gone quiet, then fade in.
// 9 Oct 2026 (David: "the cart could be a bit smoother"): fill the preview from the page the shopper is on,
// so the sheet shows THIS shirt the instant Add is pressed instead of an empty panel for ~0.9s.
function preview(form) {
  const skel = dialog.querySelector(".kd-sheet-skel");
  if (!skel) return;
  const scope = form?.closest(".shopify-section, dialog, product-card") || document;
  const pick = (sel) => scope.querySelector(sel) || document.querySelector(sel);
  const img = pick(".product-media img, media-gallery img, .product-information img, [class*=media-gallery] img, [class*=product-media] img");
  const title = pick("h1, .product-title, [class*=product__title]");
  let size = "";
  const checked = scope.querySelector("variant-picker input:checked, [class*=variant] input[type=radio]:checked, fieldset input:checked");
  if (checked) size = checked.value || "";
  let print = "";
  try {
    const fd = form ? new FormData(form.tagName === "FORM" ? form : form.querySelector("form")) : null;
    const nm = fd && (fd.get("properties[Name]") || "");
    const no = fd && (fd.get("properties[Number]") || "");
    if (nm || no) print = (String(nm) + " " + String(no)).trim();
  } catch (e) {}
  const im = skel.querySelector("img");
  const src = img ? (img.currentSrc || img.src || "") : "";
  if (im) { if (src) { im.src = src; im.style.visibility = ""; } else im.style.visibility = "hidden"; }
  skel.querySelector(".kd-sheet-skel__t").textContent = title ? title.textContent.trim() : "";
  skel.querySelector(".kd-sheet-skel__v").textContent = [size && ("Size " + size), print].filter(Boolean).join(" · ");
}
function headText(t) {
  const sp = dialog.querySelector(".kd-sheet-ok span");
  if (sp && sp.textContent !== t) sp.textContent = t;
}

function holdUntilFresh(promise) {
  dialog.classList.add('kd-sheet--loading');
  dialog.classList.remove('kd-sheet--done');
  headText('Adding to your bag');
  let quiet, mo;
  const reveal = () => {
    clearTimeout(quiet); clearTimeout(hard); mo?.disconnect();
    dialog.classList.remove('kd-sheet--loading');
    headText('Added to your bag');
    dialog.classList.add('kd-sheet--done');
  };
  // 9 Oct 2026: the sheet now opens before the cart answers, even on an empty cart, so never reveal on a timer
  // while the add is still in flight (a 4s test add showed "Added to your bag" over "Your cart is empty").
  // The long backstop only covers a promise that never settles.
  const hard = setTimeout(reveal, 12000);
  const settle = () => {
    mo = new MutationObserver(() => { clearTimeout(quiet); quiet = setTimeout(reveal, 70); });
    mo.observe(dialog, { subtree: true, childList: true, characterData: true });
    quiet = setTimeout(reveal, 90);
  };
  const fail = () => { clearTimeout(hard); clearTimeout(quiet); mo?.disconnect(); dialog.classList.remove('kd-sheet--loading'); drawer.close(); };
  if (promise && promise.then) promise.then((res) => { if (res && res.detail && res.detail.didError) fail(); else settle(); }, fail); else settle();
}

if (drawer && dialog) {
  build();

  // capture: runs before the drawer's own listener, so the class is on before it opens
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.action !== 'add') return;
    event.promise?.then(({ detail } = {}) => { if (!detail?.didError) tagCart(); }).catch(() => {});
    if (ARM !== 'sheet' || !PHONE.matches || drawer.isOpen || drawer.hasAttribute('open')) return;
    dialog.classList.add('kd-sheet');
    preview(event.target instanceof Element ? event.target : null);
    holdUntilFresh(event.promise);
    // if the add fails and the drawer never opens, the next open (header bag) must be the full cart
    setTimeout(() => { if (!drawer.hasAttribute('open')) dialog.classList.remove('kd-sheet'); }, 6000);
  }, { capture: true });

  // any other way in (header bag, minibar pill) is the full cart; closing resets the sheet
  dialog.addEventListener('close', () => {
    dialog.classList.remove('kd-sheet', 'kd-sheet--growing', 'kd-sheet--loading', 'kd-sheet--done');
    dialog.style.height = '';
    dialog.style.translate = '';
    headText('Added to your bag');
  });
}
